import { Effect, Schema as S, SchemaTransformation } from "effect";

/** Server emits `nextCursor: null` (search) where v1 typed `undefined`; decode to v1's type. */
export const NullableCursor = S.NullOr(S.Number).pipe(
	S.decodeTo(
		S.UndefinedOr(S.Number),
		SchemaTransformation.transform({
			decode: (value) => value ?? undefined,
			encode: (value) => value ?? null,
		}),
	),
);

/** A key the server omits (or nulls) but v1 typed as present-or-null. */
export const NullByDefault = S.NullOr(S.String).pipe(
	S.withDecodingDefaultKey(Effect.succeed(null)),
);

/** Server emits `null` where v1 typed `string` (entry `title`); decode to `""`. */
export const NullAsEmpty = S.NullOr(S.String).pipe(
	S.decodeTo(
		S.String,
		SchemaTransformation.transform({
			decode: (value) => value ?? "",
			encode: (value) => value,
		}),
	),
);

/** v1 `ApiResponse<T>`: a cursor page. Server sends `nextCursor: null` (search) or omits it (records, entries). */
export const Page = <Item extends S.Top>(item: Item) =>
	S.Struct({
		data: S.Array(item),
		nextCursor: S.optionalKey(NullableCursor),
	});

export const Success = S.Struct({ success: S.Boolean });

export const Avatar = S.Struct({
	exists: S.Boolean,
	imageUrl: S.NullOr(S.String),
});

export const RecordType = S.Literals(["person", "group", "entity", "effort"]);

/** Entry types v1 knows how to render. The server's `object.type` column is a free
 * string (legacy rows carry "knot"), so the wire schema accepts any string and
 * views fall back to a generic rendering for values outside this list. */
export const ENTRY_TYPES = [
	"note",
	"tasks",
	"upload",
	"update",
	"learning",
	"risk",
	"decision",
	"accomplishment",
	"hypothesis",
	"experiment",
] as const;
export type KnownEntryType = (typeof ENTRY_TYPES)[number];
export const EntryType = S.String;
export type EntryType = string;

/** Server `core/provenance.ts` AgentBlock: which agent made a write. */
export const AgentBlock = S.Struct({
	tool: S.optionalKey(
		S.Struct({ name: S.String, version: S.optionalKey(S.String) }),
	),
	grant: S.optionalKey(
		S.Struct({
			kind: S.String,
			id: S.optionalKey(S.String),
			label: S.optionalKey(S.String),
		}),
	),
	vendor: S.optionalKey(S.String),
	model: S.optionalKey(S.String),
	reported: S.optionalKey(S.Literals(["agent", "server"])),
});
export type AgentBlock = typeof AgentBlock.Type;

export const ProvenancePerson = S.Struct({
	id: S.String,
	name: S.NullOr(S.String),
	imageUrl: S.NullOr(S.String),
});
export type ProvenancePerson = typeof ProvenancePerson.Type;

/** Who made a write and, for agent channels, which agent (server `Provenance`). */
export const Provenance = S.Struct({
	channel: S.String,
	person: S.NullOr(ProvenancePerson),
	agent: S.NullOr(AgentBlock),
});
export type Provenance = typeof Provenance.Type;

/** v1 `HistoryVersion<T>`. Server: `getObjectHistory` in shell/db/historyQueries.ts. */
export const HistoryVersion = <V extends S.Top>(version: V) =>
	S.Struct({
		version,
		validFrom: S.String,
		validTo: S.NullOr(S.String),
		event: S.Struct({
			id: S.String,
			datetime: S.String,
			source: S.String,
			type: S.String,
			/** Absent from responses older than the provenance feature. */
			provenance: S.optionalKey(Provenance),
		}),
	});
